// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback } from 'react';

import type { ProductId, ShopRole } from '@evolvemep/api-ts';

import { PRODUCT_IDS } from 'constants/globalConstants';
import { isNil } from 'helpers/isNotNil';
import { useUser } from 'hooks/user/useUser';

export type AccessArea =
  | 'billOfMaterials'
  | 'catalog'
  | 'companyMembers'
  | 'companySettings'
  /** Company settings only accessible to members of the Evolve company */
  | 'evolveSettings'
  | 'customStatus'
  | 'customProperties'
  | 'fabrication'
  | 'facilities'
  | 'insights'
  | 'labels'
  | 'marketing'
  | 'materialAnalytics'
  | 'modelBrowser'
  | 'projects'
  | 'savedViews'
  | 'shipments'
  | 'shopInsights'
  | 'shopSettings'
  /** Used for features that require the cloud spooling license, such as spool requests */
  | 'spoolCreation'
  /** Used when assigning spools */
  | 'spooling'
  | 'tasks'
  | 'timeTracking'
  | 'workCellSetup'
  | 'workOrders';

type AccessData = {
  readOnly?: [ShopRole, ...ShopRole[]];
  write: [ShopRole, ...ShopRole[]];
  /** If specified, the user must have a license for at least one of the specified products to access the area. */
  requiresLicense?: ProductId[];
  isEvolveOnly?: boolean;
};

/**
 * Lookup of access areas and their associated roles.
 * Company Administrators implicitly have full access to all areas,
 * though "Company.Administrator" should still be explicitly listed if it's the only allowed role.
 */
const roleAccessLookup: Record<AccessArea, AccessData> = {
  billOfMaterials: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  catalog: {
    write: ['Shop.FullAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  companyMembers: {
    readOnly: ['Shop.ShopFloor', 'Shop.FullAccess', 'Shop.LimitedAccess'],
    write: ['Company.Administrator'],
  },
  companySettings: {
    write: ['Company.Administrator'],
  },
  customStatus: {
    write: ['Company.Administrator', 'Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess'],
  },
  customProperties: {
    write: ['Shop.FullAccess'],
  },
  evolveSettings: {
    write: ['Company.Administrator'],
    isEvolveOnly: true,
  },
  fabrication: {
    /** On the backend, Shop.LimitedAccess can also read fabrication item data, but as of this time we don't want limited access users performing fabrication tasks  */
    readOnly: ['Shop.ShopFloor'],
    /**
     * Only users with full access can perform fabrication tasks.
     * TODO: Shop floor users will likely be added to this list.
     **/
    write: ['Shop.FullAccess'],
    requiresLicense: [PRODUCT_IDS.FABRICATION_LICENSE],
  },
  facilities: {
    write: ['Shop.FullAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE, PRODUCT_IDS.FABRICATION_LICENSE],
  },
  insights: {
    write: ['Company.Administrator'],
  },
  labels: {
    write: ['Shop.FullAccess'],
  },
  materialAnalytics: {
    write: ['Company.Administrator'],
    requiresLicense: [PRODUCT_IDS.MATERIAL_ANALYTICS],
  },
  marketing: {
    write: ['Evolve.MarketingManager'],
    isEvolveOnly: true,
  },
  modelBrowser: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE, PRODUCT_IDS.FABRICATION_LICENSE],
  },
  projects: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE, PRODUCT_IDS.FABRICATION_LICENSE],
  },
  savedViews: {
    write: ['Shop.FullAccess'],
  },
  shipments: {
    write: ['Shop.FullAccess', 'Shop.LimitedAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  shopInsights: {
    write: ['Company.Administrator'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  shopSettings: {
    write: ['Company.Administrator'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  spoolCreation: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess'],
    requiresLicense: [PRODUCT_IDS.WEB_SPOOLING_LICENSE],
  },
  spooling: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  tasks: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  timeTracking: {
    write: ['Shop.FullAccess', 'Shop.ShopFloor'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  workCellSetup: {
    write: ['Shop.FullAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
  workOrders: {
    /**
     * In reality, Shop.LimitedAccess and Shop.ShopFloor have some write access for work orders.
     * Refer to the work order truth table for details.
     */
    readOnly: ['Shop.LimitedAccess', 'Shop.ShopFloor'],
    write: ['Shop.FullAccess'],
    requiresLicense: [PRODUCT_IDS.SHOP_LICENSE],
  },
};

export type AccessLevel = 'read' | 'write';

/**
 * Helper function to get the roles that have access to a given area.
 * @param accessArea The area the user is trying to access, e.g., 'catalog', 'companyMembers', 'companySettings',
 * @param access the type of access the user is trying to perform while in this area, e.g., 'read' or 'write'
 * Roles with write access are implicitly allowed read access as well.
 * They will be included alongside read access roles if "read" is passed in.
 */
export const getRolesWithAccess = (accessArea: AccessArea, access: AccessLevel): [ShopRole, ...ShopRole[]] =>
  access === 'write'
    ? roleAccessLookup[accessArea].write
    : [...roleAccessLookup[accessArea].write, ...(roleAccessLookup[accessArea].readOnly ?? [])];

/**
 * Returns a function to check if the user has access to a given area.
 */
export const useGetHasAccess = () => {
  const {
    userHasRoles,
    userHasLicense,
    user: { isEvolveCompanyMember },
  } = useUser();

  const hasAccess = useCallback(
    (accessArea: AccessArea, access: AccessLevel) => {
      if (roleAccessLookup[accessArea].isEvolveOnly && !isEvolveCompanyMember) return false;
      const requiredLicense = roleAccessLookup[accessArea].requiresLicense;
      return (
        userHasRoles(...getRolesWithAccess(accessArea, access)) &&
        (isNil(requiredLicense) ||
          requiredLicense.length === 0 ||
          requiredLicense.some((license) => userHasLicense(license)))
      );
    },
    [userHasRoles, userHasLicense, isEvolveCompanyMember],
  );
  return { hasAccess };
};

/**
 * This hook returns true if the user has access to a given area.
 * @param accessArea The area the user is trying to access, e.g., 'catalog', 'companyMembers', 'companySettings',
 * @param access the type of access the user is trying to perform while in this area, e.g., 'read' or 'write'
 */
export const useHasAccess = (accessArea: AccessArea, access: AccessLevel) => {
  const { hasAccess } = useGetHasAccess();
  return useMemo(() => hasAccess(accessArea, access), [accessArea, access, hasAccess]);
};
